import { useEffect, useState, type ReactElement } from "react";
import { Link, useParams } from "react-router-dom";

import { apiClient } from "../api/client";
import { useTenant } from "../contexts/TenantContext";

/**
 * Cycle 22 ADR-008 addendum (Tenant URL Routing) — STORY-O3.
 *
 * Landing page для tenant-specific URLs:
 *   - Path B (path-based):   /t/<slug>/   — отрисовывается этим компонентом
 *   - Path A (subdomain):    <slug>.it-navigator.online/ — отрисовывается этим
 *                            компонентом если TenantContext detected slug в hostname
 *
 * Displays:
 *   - Tenant identity (name + plan + branded greeting)
 *   - Quick-access shortcuts (login / catalog / contact)
 *   - 404-like fallback если tenant slug не найден в БД
 */

type TenantInfo = {
  slug: string;
  name: string;
  plan: string;
  is_active: boolean;
};

export function TenantLandingPage(): ReactElement {
  const params = useParams<{ slug?: string }>();
  const tenantCtx = useTenant();
  // Priority: URL param (path mode) → hostname (subdomain mode).
  const slug = params.slug ?? tenantCtx.slug;

  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setError("Tenant slug не определён в URL");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        // Public endpoint — returns minimal info без админских данных.
        const r = await apiClient.get(`/api/tenants/${slug}/public`);
        setTenant(r.data as TenantInfo);
        setError(null);
      } catch (e: any) {
        if (e?.response?.status === 404) {
          setError(`Клиент с slug «${slug}» не найден`);
        } else {
          setError(e?.message || "Ошибка загрузки");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <main style={pageStyle}>
        <p>Загружаем профиль клиента…</p>
      </main>
    );
  }

  if (error || !tenant) {
    return (
      <main style={pageStyle}>
        <h1 style={{ color: "#a01c2a" }}>Не найдено</h1>
        <p style={{ color: "#666" }}>{error}</p>
        <p>
          <Link to="/" style={linkStyle}>← На главную портала</Link>
        </p>
      </main>
    );
  }

  if (!tenant.is_active) {
    return (
      <main style={pageStyle}>
        <h1>{tenant.name}</h1>
        <div style={warningBoxStyle}>
          <strong>⚠️ Доступ временно ограничен</strong>
          <p style={{ marginTop: "8px" }}>
            Аккаунт {tenant.slug} приостановлен. Для активации свяжитесь с
            менеджером IT-Navigate: <a href="mailto:sales@it-navigator.online">
              sales@it-navigator.online
            </a>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main style={pageStyle}>
      <header style={headerStyle}>
        <h1 style={{ margin: 0 }}>{tenant.name}</h1>
        <span style={planBadge(tenant.plan)}>{planLabel(tenant.plan)}</span>
      </header>

      <p style={{ color: "#666", fontSize: "16px", marginTop: "8px" }}>
        Личный кабинет клиента IT-Navigate.{" "}
        <code style={codeStyle}>{tenant.slug}</code>
      </p>

      <div style={ctaGridStyle}>
        <Link to={tenantPath(tenantCtx, "/login")} style={ctaCardStyle}>
          <strong>🔐 Войти</strong>
          <span style={{ color: "#666", fontSize: "13px" }}>
            Доступ к каталогу, демо и аналитике
          </span>
        </Link>
        <a href="mailto:sales@it-navigator.online" style={ctaCardStyle}>
          <strong>💬 Связаться</strong>
          <span style={{ color: "#666", fontSize: "13px" }}>
            Вопросы по интеграции, биллингу, расширению
          </span>
        </a>
        <Link to={tenantPath(tenantCtx, "/")} style={ctaCardStyle}>
          <strong>🏠 Главная портала</strong>
          <span style={{ color: "#666", fontSize: "13px" }}>
            Каталог продуктов, кейсы, отзывы
          </span>
        </Link>
      </div>

      <TenantProductsCard slug={tenant.slug} />

      <footer style={footerStyle}>
        <p>
          Контекст: <code style={codeStyle}>tenant={tenant.slug}</code>{" "}
          (source: {tenantCtx.source}).
          {tenantCtx.source === "path" && (
            <>
              {" "}
              При активации <code>*.it-navigator.online</code> wildcard DNS,
              этот URL будет доступен как{" "}
              <code>{tenant.slug}.it-navigator.online</code>.
            </>
          )}
        </p>
      </footer>
    </main>
  );
}

// ─── Products card (ADR-009) ────────────────────────────────────────────

function TenantProductsCard({ slug }: { slug: string }): ReactElement | null {
  const [items, setItems] = useState<{ product: string; status: string }[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get(`/api/tenants/${slug}/products`);
        setItems((r.data?.items as { product: string; status: string }[]) ?? []);
      } catch {
        /* молча — карточка просто не покажется */
      }
    })();
  }, [slug]);

  const rosiinka = items.find((i) => i.product === "rosiinka");
  if (!rosiinka) return null;
  const active = rosiinka.status === "active";

  return (
    <div style={productCardStyle}>
      <strong>🤖 Росиинка — AI-API</strong>
      <p style={{ color: "#666", fontSize: "14px", margin: "6px 0 0" }}>
        {active
          ? "Активна — рабочий доступ к AI-API. API-ключ и инструкции — у вашего менеджера IT-Navigate."
          : "Доступна для подключения — обратитесь к менеджеру IT-Navigate для активации."}
      </p>
    </div>
  );
}

const productCardStyle: React.CSSProperties = {
  marginTop: "24px",
  padding: "18px",
  border: "1px solid #d8e2ff",
  borderRadius: "8px",
  background: "#f5f8ff",
};

// ─── Helpers ───────────────────────────────────────────────────────────

function tenantPath(
  ctx: { isSubdomainTenant: boolean; slug: string | null },
  path: string
): string {
  // On subdomain → uses plain paths (subdomain provides context)
  // On path-based → prefix /t/<slug>/<path>
  if (ctx.isSubdomainTenant || !ctx.slug) return path;
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/t/${ctx.slug}${cleanPath === "/" ? "" : cleanPath}`;
}

function planLabel(plan: string): string {
  const labels: Record<string, string> = {
    trial: "Trial (14 дней)",
    free: "Free",
    pro: "Pro",
    enterprise: "Enterprise",
  };
  return labels[plan] ?? plan;
}

function planBadge(plan: string): React.CSSProperties {
  const DEFAULT = { bg: "#eee", fg: "#666" };
  const colors: Record<string, { bg: string; fg: string }> = {
    trial: { bg: "#fff7d6", fg: "#a07000" },
    free: { bg: "#eee", fg: "#666" },
    pro: { bg: "#e6f7ee", fg: "#0a7a4a" },
    enterprise: { bg: "#e6f0ff", fg: "#0b5fff" },
  };
  const c = colors[plan] ?? DEFAULT;
  return {
    background: c.bg,
    color: c.fg,
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: 600,
  };
}

const pageStyle: React.CSSProperties = {
  maxWidth: "720px",
  margin: "60px auto",
  padding: "40px 30px",
  fontFamily: "system-ui",
  lineHeight: 1.5,
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
};

const ctaGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "16px",
  marginTop: "30px",
};

const ctaCardStyle: React.CSSProperties = {
  display: "block",
  padding: "20px",
  border: "1px solid #ddd",
  borderRadius: "8px",
  textDecoration: "none",
  color: "#222",
  background: "#fff",
};

const warningBoxStyle: React.CSSProperties = {
  background: "#fff7d6",
  border: "1px solid #f0c000",
  borderRadius: "8px",
  padding: "16px",
  marginTop: "20px",
};

const footerStyle: React.CSSProperties = {
  marginTop: "60px",
  paddingTop: "20px",
  borderTop: "1px solid #eee",
  color: "#888",
  fontSize: "13px",
};

const codeStyle: React.CSSProperties = {
  background: "#f5f5f5",
  padding: "2px 6px",
  borderRadius: "3px",
  fontSize: "13px",
};

const linkStyle: React.CSSProperties = {
  color: "#0b5fff",
  textDecoration: "none",
};
